"use client";

import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { badgeColors } from "@/lib";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { FaChevronDown, FaChevronUp, FaXmark } from "react-icons/fa6";

type Item = { id: string; name: string };

export type MultiSelectBadgesProps = {
  name: string;
  label?: string;
  options: Item[];
  placeholder?: string;
  className?: string;
  classNameContainer?: string;
  required?: boolean;
  noBorder?: boolean;
  noShadow?: boolean;
};

export function MultiSelectBadges({
  name,
  label,
  options,
  placeholder = "Select...",
  className = "",
  classNameContainer = "",
  required = false,
  noBorder = false,
  noShadow = false,
}: MultiSelectBadgesProps) {
  const [open, setOpen] = useState(false);
  const { control } = useFormContext();
  const {
    field: { value = [], onChange },
    fieldState: { error },
  } = useController({ name, control });

  const selectedItems: Item[] = Array.isArray(value) ? value : [];

  const handleSelect = (item: Item) => {
    const alreadySelected = selectedItems.find((i) => i.id === item.id);
    if (alreadySelected) {
      onChange(selectedItems.filter((i) => i.id !== item.id));
    } else {
      onChange([...selectedItems, item]);
    }
  };

  const handleRemoveBadge = (idToRemove: string) => {
    onChange(selectedItems.filter((i) => i.id !== idToRemove));
  };

  return (
    <div className={cn("mt-3", classNameContainer)}>
      {label && (
        <label
          htmlFor={name}
          className="block mb-2 text-sm text-gray-600 font-semibold"
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
      <div
            id={name}
            className={cn(
        "flex min-h-[40px] items-center gap-2 w-full px-4 py-2 rounded-md cursor-pointer text-sm",
              !noShadow && "shadow-sm",
              error && !noBorder ? "border-red-500" : "",
              !error && !noBorder ? "border-gray-200" : "",
              !noBorder && "border",
              "focus-within:ring-1 focus-within:ring-brand-500 focus-within:border-brand-500",
              className?.includes('bg-') ? "" : "bg-white",
              className
            )}
            tabIndex={0}
            role="combobox"
            aria-expanded={open}
            aria-controls="suggestion-list"
          >
            <div className="flex flex-wrap gap-2 flex-grow">
              {selectedItems.length > 0 ? (
                selectedItems.map((item, index) => (
                  <Badge
                    key={item.id}
                    className={cn(
                      "flex items-center gap-1 pr-1 cursor-pointer",
                      badgeColors[index % badgeColors.length]
                    )}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleRemoveBadge(item.id);
                    }}
                  >
                    {item.name}
                    <FaXmark className="h-3 w-3 ml-1" />
                  </Badge>
                ))
              ) : (
                <span className="text-gray-400 text-sm">{placeholder}</span>
              )}
            </div>
            <span className="ml-auto text-gray-400 flex-shrink-0">
              {open ? (
                <FaChevronUp className="text-xs" />
              ) : (
                <FaChevronDown className="text-xs" />
              )}
            </span>
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-white z-50 max-h-[300px] overflow-y-auto">
          <Command>
            <CommandList>
              <CommandEmpty>There are no options.</CommandEmpty>
              <CommandGroup>
                {options.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={item.name}
                    className={cn(
                      "cursor-pointer px-4 py-2 hover:bg-gray-100",
                      selectedItems.some((i) => i.id === item.id) &&
                        "bg-brand-100 text-brand-600 font-semibold"
                    )}
                    onSelect={() => handleSelect(item)}
                  >
                    {item.name}
                    {selectedItems.some((i) => i.id === item.id) && (
                      <span className="ml-auto h-2 w-2 rounded-full bg-brand-500" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error && (
        <span className="text-red-500 text-xs mt-1 block">{error.message}</span>
      )}
    </div>
  );
}
